import React, { useMemo, useRef, useState } from 'react';
import { Download, Upload } from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';
import { PartForm } from '../forms/PartForm';
import { Part, PartCategory } from '../../types';
import { downloadTextFile, getCsvCell, makeImportId, parseCsvRecords, parseNumber, toCsv } from '../../utils/dataExchange';
import '../../styles/enhanced.css';

const partCategories: PartCategory[] = [
  'Engine',
  'Transmission',
  'Brakes',
  'Tires',
  'Electrical',
  'Fluids',
  'Filters',
  'Body',
  'Suspension',
  'Other'
];

const normalizePartCategory = (value: string): PartCategory => {
  const matchedCategory = partCategories.find(category => category.toLowerCase() === value.toLowerCase());
  return matchedCategory || 'Other';
};

const PartsPage: React.FC = () => {
  const { state, addPart, updatePart, deletePart } = useAppContext();
  const { parts, currentUser } = state;
  const importInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPartForm, setShowPartForm] = useState(false);
  const [editingPart, setEditingPart] = useState<Part | null>(null);
  const [partPendingDelete, setPartPendingDelete] = useState<Part | null>(null);
  const [importFeedback, setImportFeedback] = useState<{ tone: 'success' | 'warning' | 'danger'; text: string; details?: string[] } | null>(null);

  const filteredParts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return parts;
    }

    return parts.filter((part: Part) =>
      part.name.toLowerCase().includes(term) ||
      part.partNumber.toLowerCase().includes(term) ||
      (part.supplier || '').toLowerCase().includes(term) ||
      (part.location || '').toLowerCase().includes(term)
    );
  }, [parts, searchTerm]);

  const handleAddPart = () => {
    setEditingPart(null);
    setShowPartForm(true);
  };

  const handleEditPart = (part: Part) => {
    setEditingPart(part);
    setShowPartForm(true);
  };

  const confirmDeletePart = async () => {
    if (!partPendingDelete) return;
    try {
      await deletePart(partPendingDelete.id);
      setPartPendingDelete(null);
    } catch (error) {
      console.error('Failed to delete part:', error);
      alert('Failed to delete part. Please try again.');
    }
  };

  const handleFormSuccess = (part: Part) => {
    setShowPartForm(false);
    setEditingPart(null);
  };

  const handleFormCancel = () => {
    setShowPartForm(false);
    setEditingPart(null);
  };

  const handleExportParts = () => {
    const headers = [
      'ID',
      'Name',
      'Part Number',
      'Category',
      'Cost',
      'Supplier',
      'Supplier Part Number',
      'Inventory',
      'Min Stock',
      'Location'
    ];
    const rows = filteredParts.map((part: Part) => [
      part.id,
      part.name,
      part.partNumber,
      part.category,
      part.cost,
      part.supplier || '',
      part.supplierPartNumber || '',
      part.inventoryLevel ?? '',
      part.minStockLevel ?? '',
      part.location || ''
    ]);

    downloadTextFile(
      `parts-export-${new Date().toISOString().split('T')[0]}.csv`,
      toCsv(headers, rows),
      'text/csv;charset=utf-8;'
    );
    setImportFeedback({
      tone: 'success',
      text: `Exported ${filteredParts.length} part record${filteredParts.length === 1 ? '' : 's'}.`
    });
  };

  const handleImportPartsClick = () => {
    importInputRef.current?.click();
  };

  const handleImportParts = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const { records } = parseCsvRecords(text);
      const knownParts = [...parts];
      const actor = state.currentUser?.id || 'import';
      const now = new Date();
      const errors: string[] = [];
      let added = 0;
      let updated = 0;

      if (records.length === 0) {
        setImportFeedback({ tone: 'warning', text: 'No part rows were found in that CSV.' });
        return;
      }

      for (let index = 0; index < records.length; index += 1) {
        const record = records[index];
        const rowNumber = index + 2;
        const id = getCsvCell(record, ['ID', 'Part ID']);
        const name = getCsvCell(record, ['Name', 'Part Name']);
        const partNumber = getCsvCell(record, ['Part Number', 'SKU', 'Item Number']);
        const category = normalizePartCategory(getCsvCell(record, ['Category', 'Type']));
        const cost = Math.max(0, parseNumber(getCsvCell(record, ['Cost', 'Unit Cost', 'Price']), 0));
        const supplier = getCsvCell(record, ['Supplier', 'Vendor']);
        const supplierPartNumber = getCsvCell(record, ['Supplier Part Number', 'Vendor Part Number']);
        const inventoryLevel = Math.max(0, Math.trunc(parseNumber(getCsvCell(record, ['Inventory', 'Inventory Level', 'Quantity', 'Stock']), 0)));
        const minStockLevel = Math.max(0, Math.trunc(parseNumber(getCsvCell(record, ['Min Stock', 'Minimum Stock', 'Reorder Point']), 0)));
        const location = getCsvCell(record, ['Location', 'Bin', 'Shelf']);

        const missingFields = [
          !name && 'Name',
          !partNumber && 'Part Number'
        ].filter(Boolean);

        if (missingFields.length > 0) {
          errors.push(`Row ${rowNumber}: missing ${missingFields.join(', ')}.`);
          continue;
        }

        const existingPart = knownParts.find(part =>
          (id && part.id === id) ||
          part.partNumber.toLowerCase() === partNumber.toLowerCase()
        );

        if (existingPart) {
          const updates: Partial<Part> = {
            name,
            partNumber,
            category,
            cost,
            supplier,
            supplierPartNumber,
            inventoryLevel,
            minStockLevel,
            location,
            updatedAt: now,
            updatedBy: actor
          };

          await updatePart(existingPart.id, updates);
          Object.assign(existingPart, updates);
          updated += 1;
          continue;
        }

        const newPart: Part = {
          id: id || makeImportId('part', rowNumber),
          name,
          partNumber,
          category,
          cost,
          supplier,
          supplierPartNumber,
          inventoryLevel,
          minStockLevel,
          location,
          customFields: {},
          createdAt: now,
          createdBy: actor,
          updatedAt: now,
          updatedBy: actor
        };

        await addPart(newPart);
        knownParts.unshift(newPart);
        added += 1;
      }

      const skipped = errors.length;
      setImportFeedback({
        tone: skipped > 0 ? (added + updated > 0 ? 'warning' : 'danger') : 'success',
        text: `Import complete: ${added} added, ${updated} updated, ${skipped} skipped.`,
        details: errors.slice(0, 8)
      });
    } catch (error) {
      setImportFeedback({
        tone: 'danger',
        text: error instanceof Error ? error.message : 'Parts import failed. Please check the CSV and try again.'
      });
    } finally {
      event.target.value = '';
    }
  };

  if (!currentUser) {
    return (
      <div className="page-container">
        <h1>Parts & Inventory</h1>
        <p>Please log in to view and manage your parts inventory.</p>
      </div>
    );
  }

  return (
    <div className="page-container parts-page">
      <div className="page-header">
        <div>
          <h1>Parts & Inventory</h1>
          <p>Manage inventory items, track stock levels, and keep your parts data up to date.</p>
        </div>
        <div className="page-actions">
          <input
            ref={importInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleImportParts}
            style={{ display: 'none' }}
          />
          <button type="button" className="btn-secondary" onClick={handleExportParts}>
            <Download size={16} />
            Export CSV
          </button>
          <button type="button" className="btn-secondary" onClick={handleImportPartsClick}>
            <Upload size={16} />
            Import CSV
          </button>
          <button type="button" className="btn-primary" onClick={handleAddPart}>
            Add Part
          </button>
        </div>
      </div>

      {importFeedback && (
        <div className={`import-feedback ${importFeedback.tone}`} role="status">
          <strong>{importFeedback.text}</strong>
          {importFeedback.details && importFeedback.details.length > 0 && (
            <ul>
              {importFeedback.details.map(detail => (
                <li key={detail}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="page-filters">
        <input
          type="search"
          placeholder="Search parts by name, number, supplier, or location"
          value={searchTerm}
          onChange={event => setSearchTerm(event.target.value)}
          className="search-input"
        />
      </div>

      {filteredParts.length === 0 ? (
        <div className="empty-state">
          <p>{parts.length === 0 ? 'No parts added yet.' : 'No matching parts found.'}</p>
        </div>
      ) : (
        <div className="data-table-wrapper">
          <table className="data-table parts-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Part Number</th>
                <th>Category</th>
                <th>Inventory</th>
                <th>Min Stock</th>
                <th>Supplier</th>
                <th>Location</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredParts.map((part: Part) => (
                <tr key={part.id}>
                  <td>{part.name}</td>
                  <td>{part.partNumber}</td>
                  <td>{part.category}</td>
                  <td>{part.inventoryLevel ?? '—'}</td>
                  <td>{part.minStockLevel ?? '—'}</td>
                  <td>{part.supplier || '—'}</td>
                  <td>{part.location || '—'}</td>
                  <td>
                    <button type="button" className="btn-secondary small" onClick={() => handleEditPart(part)}>
                      Edit
                    </button>
                    <button type="button" className="btn-danger small" onClick={() => setPartPendingDelete(part)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showPartForm && (
        <PartForm part={editingPart ?? undefined} onSuccess={handleFormSuccess} onCancel={handleFormCancel} />
      )}

      {partPendingDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Delete Part</h2>
              <button type="button" onClick={() => setPartPendingDelete(null)} className="close-btn">&times;</button>
            </div>
            <p>
              Delete {partPendingDelete.name}? This will remove the part from inventory and cannot be undone.
            </p>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setPartPendingDelete(null)}>
                Cancel
              </button>
              <button type="button" className="btn-danger" onClick={confirmDeletePart}>
                Delete Part
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartsPage;
